import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area,
} from "recharts";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Spinner } from "./ui";
import { formatMoney } from "../lib/utils";
import { LineChart, ArrowUpRight } from "lucide-react";

const POS = "#34D399";
const NEG = "#F87171";

export default function NetWorthWidget({ className = "lg:col-span-5 flex flex-col" }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/portfolio/overview?range=3M")
      .then(({ data }) => { if (active) setItems(data); })
      .catch(() => { if (active) setItems([]); });
    return () => { active = false; };
  }, []);

  const total = (items || []).reduce((s, e) => s + (e.net_worth || 0), 0);
  const baseCur = items?.[0]?.base_currency || "EUR";
  const fxAgg = {};
  (items || []).forEach((e) => Object.entries(e.by_currency || {}).forEach(([c, v]) => { fxAgg[c] = (fxAgg[c] || 0) + v; }));
  const converted = Object.entries(fxAgg)
    .filter(([c, v]) => c !== baseCur && Math.abs(v) >= 1)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));

  return (
    <Card className={className} data-testid="networth-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <LineChart className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-medium">{t("portfolio_net_worth")}</h3>
        </div>
        <button onClick={() => navigate("/entity/holding")} className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1" data-testid="networth-detail">
          {t("detail")} <ArrowUpRight className="w-3 h-3" />
        </button>
      </div>

      {items === null ? (
        <div className="flex-1 flex items-center justify-center py-8"><Spinner className="w-5 h-5 text-text-secondary" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-text-secondary py-6 text-center">{t("portfolio_no_holdings")}</p>
      ) : (
        <>
          <div className="mb-3 pb-3 border-b">
            <div className="text-3xl font-heading font-medium tabular-nums tracking-tight" data-testid="networth-total">{formatMoney(total, baseCur)}</div>
            {converted.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="networth-fx-breakdown">
                <span className="text-[11px] text-text-secondary">{t("networth_incl")}</span>
                {converted.map(([cur, amt]) => (
                  <span key={cur} data-testid={`networth-fx-${cur}`}
                    className="text-[11px] px-1.5 py-0.5 rounded-full border text-text-secondary tabular-nums">
                    {formatMoney(amt, cur, { decimals: 0 })}
                  </span>
                ))}
                <span className="text-[11px] text-text-secondary">{t("networth_converted")}</span>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {items.map((e) => {
              const up = (e.change_pct ?? 0) >= 0;
              const color = up ? POS : NEG;
              const spark = (e.sparkline || []).map((v, i) => ({ i, v }));
              return (
                <button key={e.entity_id} onClick={() => navigate(`/entity/${e.key}`)}
                  data-testid={`networth-row-${e.key}`}
                  className="w-full flex items-center gap-4 p-2 -mx-2 rounded-xl hover:bg-surface-hover transition-colors text-left">
                  <span className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: e.color || "#2563EB" }} />
                    <span className="text-sm font-medium truncate">{e.name}</span>
                  </span>
                  <div className="w-20 h-9 shrink-0">
                    {spark.length > 1 && (
                      <ResponsiveContainer>
                        <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id={`nw-${e.key}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                              <stop offset="100%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#nw-${e.key})`} isAnimationActive={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <span className="text-right shrink-0">
                    <span className="block text-sm font-medium tabular-nums">{formatMoney(e.net_worth, e.base_currency)}</span>
                    {e.change_pct !== null && e.change_pct !== undefined && (
                      <span className="block text-xs font-medium tabular-nums" style={{ color }}>
                        {up ? "+" : ""}{e.change_pct}%
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}
